import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '@/contexts/auth-context'
import { useAuthActions } from '@convex-dev/auth/react'
import { useNavigate } from 'react-router-dom'
import { isElectron } from '@/lib/electron'
import { motion } from 'motion/react'
import { Terminal, ChevronRight, Wand2, Clock, FolderSearch, MessageSquare, Copy, Check, Download, CreditCard } from 'lucide-react'

import { Navbar } from '@/components/homepage/sections/Navbar'
import { HomepageFooter } from '@/components/homepage/sections/HomepageFooter'
import { CornerPlus } from '@/components/ui/corner-plus'
import { HeaderBadge } from '@/components/homepage/ui/HeaderBadge'
import { Highlight } from '@/components/homepage/_config'
import { cn } from '@/lib/utils'
import { OG_IMAGE_URL, SITE_HOST, siteEmail, siteUrl } from '@/lib/config/site'

// ─── Typing animation for the terminal ─────────────────────────────────

function useTypingAnimation(lines: { text: string; delay: number }[], loop = true) {
  const [displayedLines, setDisplayedLines] = useState<string[]>([])
  const [currentLineIndex, setCurrentLineIndex] = useState(0)
  const [currentCharIndex, setCurrentCharIndex] = useState(0)
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    if (currentLineIndex >= lines.length) {
      setIsComplete(true)
      if (loop) {
        const timeout = setTimeout(() => {
          setDisplayedLines([])
          setCurrentLineIndex(0)
          setCurrentCharIndex(0)
          setIsComplete(false)
        }, 4000)
        return () => clearTimeout(timeout)
      }
      return
    }

    const currentLine = lines[currentLineIndex]

    if (currentCharIndex === 0 && currentLine.delay > 0) {
      const delayTimeout = setTimeout(() => {
        setCurrentCharIndex(1)
      }, currentLine.delay)
      return () => clearTimeout(delayTimeout)
    }

    if (currentCharIndex <= currentLine.text.length) {
      const speed = currentLine.text[currentCharIndex - 1] === ' ' ? 20 : 35
      const timeout = setTimeout(() => {
        setDisplayedLines(prev => {
          const updated = [...prev]
          // Fill any gaps with empty strings to avoid sparse array
          while (updated.length <= currentLineIndex) {
            updated.push('')
          }
          updated[currentLineIndex] = currentLine.text.slice(0, currentCharIndex)
          return updated
        })
        setCurrentCharIndex(prev => prev + 1)
      }, speed)
      return () => clearTimeout(timeout)
    }

    // Line complete — move to next
    const nextTimeout = setTimeout(() => {
      setCurrentLineIndex(prev => prev + 1)
      setCurrentCharIndex(0)
    }, 300)
    return () => clearTimeout(nextTimeout)
  }, [currentLineIndex, currentCharIndex, lines, loop])

  return { displayedLines, isComplete }
}

// ─── Terminal block ─────────────────────────────────────────────────────

const terminalLines = [
  { text: '$ footage login', delay: 600 },
  { text: '  Opening browser...', delay: 400 },
  { text: `  Logged in as ${siteEmail('ashot')}`, delay: 500 },
  { text: '', delay: 200 },
  { text: '$ footage scan ~/Movies', delay: 800 },
  { text: '  Scanning 47 files...', delay: 400 },
  { text: '  Found 12 video clips, 8 photos', delay: 300 },
  { text: '', delay: 200 },
  { text: '$ footage "make a highlight reel from my best drone shots"', delay: 800 },
  { text: '  Attaching to film misty-dawn...', delay: 400 },
  { text: '  Creating 4 clips from your footage', delay: 600 },
  { text: '  → Clip 1: Mountain flyover at sunrise', delay: 300 },
  { text: '  → Clip 2: Coastal descent through fog', delay: 300 },
  { text: '  → Clip 3: Forest canopy pull-back', delay: 300 },
  { text: '  → Clip 4: Valley reveal with lens flare', delay: 300 },
]

function TerminalDemo() {
  const { displayedLines, isComplete } = useTypingAnimation(terminalLines)

  return (
    <div className="relative rounded-lg border border-border bg-background overflow-hidden shadow-2xl">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card/50">
        <div className="flex gap-1.5">
          <div className="size-3 rounded-full bg-red-500/60" />
          <div className="size-3 rounded-full bg-yellow-500/60" />
          <div className="size-3 rounded-full bg-green-500/60" />
        </div>
        <span className="text-xs text-muted-foreground font-mono ml-2">footage-cli</span>
      </div>
      {/* Terminal content */}
      <div className="p-5 font-mono text-sm leading-relaxed min-h-[320px] md:min-h-[360px]">
        {displayedLines.map((line, i) => {
          const l = line ?? ''
          return (
            <div key={i} className={cn(
              "whitespace-pre-wrap",
              l.startsWith('$') ? 'text-sky-400 font-semibold' :
              l.startsWith('  →') ? 'text-green-400' :
              l.includes('Logged in') ? 'text-green-400' :
              'text-muted-foreground'
            )}>
              {l || '\u00A0'}
            </div>
          )
        })}
        {!isComplete && (
          <motion.span
            className="inline-block w-2 h-4 bg-sky-400 ml-0.5"
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.6, repeat: Infinity, repeatType: 'reverse' }}
          />
        )}
      </div>
    </div>
  )
}

// ─── Install snippet ─────────────────────────────────────────────────────

function InstallSnippet({ className }: { className?: string }) {
  const [copied, setCopied] = useState(false)
  const command = `curl -fsSL ${SITE_HOST}/install.sh | sh`

  const handleCopy = () => {
    navigator.clipboard.writeText(command)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={cn("w-full max-w-md mx-auto", className)}>
      <button
        onClick={handleCopy}
        className={cn(
          "group flex items-center gap-3 w-full px-5 py-3.5 rounded-lg",
          "border border-border bg-card/60 hover:bg-card/80",
          "transition-all duration-200 cursor-pointer",
          "font-mono text-sm"
        )}
      >
        <span className="text-sky-400">$</span>
        <span className="flex-1 text-left text-foreground">{command}</span>
        {copied ? (
          <Check className="size-4 text-green-400" />
        ) : (
          <Copy className="size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        )}
      </button>
    </div>
  )
}

// ─── Feature card ───────────────────────────────────────────────────────

function FeatureCard({ icon, title, description, index }: {
  icon: React.ReactNode
  title: string
  description: string
  index: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "flex flex-col gap-3 p-6 md:p-8",
        "border-b border-border",
        "md:border-b-0",
        index % 2 === 0 ? "md:border-r" : "",
      )}
    >
      <div className="size-10 rounded-lg bg-accent flex items-center justify-center text-foreground">
        {icon}
      </div>
      <h3 className="text-lg font-medium tracking-tight">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </motion.div>
  )
}

// ─── Command preview pills ─────────────────────────────────────────────

const commands = [
  { cmd: 'footage chat', desc: 'AI creative director' },
  { cmd: 'footage scan', desc: 'analyze local media' },
  { cmd: 'footage films', desc: 'list & create films' },
  { cmd: 'footage status', desc: 'check clip progress' },
  { cmd: 'footage export', desc: 'render final video' },
  { cmd: 'footage credits', desc: 'check credit balance' },
]

function CommandList() {
  return (
    <div className="flex flex-wrap gap-2 justify-center">
      {commands.map((c, i) => (
        <motion.div
          key={c.cmd}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card/50 text-xs font-mono"
        >
          <ChevronRight className="size-3 text-sky-400" />
          <span className="text-foreground font-semibold">{c.cmd}</span>
          <span className="text-muted-foreground hidden sm:inline">— {c.desc}</span>
        </motion.div>
      ))}
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────

export default function CLIPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { signIn } = useAuthActions()
  const handleSignUp = () => {
    if (isElectron()) {
      navigate('/signup')
      return
    }
    signIn('google', { redirectTo: `${window.location.origin}/auth` })
  }

  const features = [
    {
      icon: <MessageSquare className="size-5" />,
      title: 'AI creative director',
      description: 'Describe what you want in natural language. Footage plans clips, picks models, and manages your video. Pipe prompts or use interactive mode.',
    },
    {
      icon: <FolderSearch className="size-5" />,
      title: 'Local media analysis',
      description: 'Point the CLI at a folder of videos and photos. It analyzes your footage and uses it as context for generating new clips and edits.',
    },
    {
      icon: <Wand2 className="size-5" />,
      title: 'Every model, one interface',
      description: 'Access Kling, Veo, Wan, and every model Footage supports directly from the command line. Footage picks the right one for each shot.',
    },
    {
      icon: <Clock className="size-5" />,
      title: 'Async generation',
      description: 'Queue generations and walk away. Check back with footage status to see progress, or keep working while clips render in the cloud.',
    },
    {
      icon: <Download className="size-5" />,
      title: 'Export and render',
      description: 'When your clips are ready, export the final cut. All rendering happens server-side — your machine stays free.',
    },
    {
      icon: <CreditCard className="size-5" />,
      title: 'Managed credits',
      description: 'Same account, same credits as the web studio. Check your balance, track usage, and manage videos across both interfaces.',
    },
  ]

  return (
    <div className="min-h-screen bg-background px-4 lg:px-0 overflow-x-hidden">
      <Helmet>
        <title>CLI - Footage</title>
        <meta name="description" content="AI video editor from your terminal. Footage CLI gives you an AI creative director that plans, generates, and manages videos from the command line." />
        <link rel="canonical" href={siteUrl('/cli')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Footage CLI - Video Generation From Your Terminal" />
        <meta property="og:description" content="AI video editor from your terminal. Footage CLI gives you an AI creative director that plans, generates, and manages videos from the command line." />
        <meta property="og:url" content={siteUrl('/cli')} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:site_name" content="Footage" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Footage CLI - Video Generation From Your Terminal" />
        <meta name="twitter:description" content="AI video editor from your terminal. Footage CLI gives you an AI creative director that plans, generates, and manages videos from the command line." />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>

      <div className="max-w-7xl mx-auto border-x border-border">
        <Navbar isAuthenticated={isAuthenticated} onSignUp={handleSignUp} />
        <main className="flex flex-col divide-y divide-border pt-16">

          {/* ── Hero ─────────────────────────────────────────────── */}
          <section className="relative flex flex-col items-center justify-center px-4 py-24 md:py-40">
            <CornerPlus position="bottom-left" className="text-muted-foreground/50" />
            <CornerPlus position="bottom-right" className="text-muted-foreground/50" />
            <div className="absolute inset-0 z-0 h-full w-full bg-radial-[at_45%_85%] from-[#2CD5FF]/20 via-[#2C30FF]/3 mask-[linear-gradient(to_bottom,transparent,black_100%)]" />
            <div className="absolute inset-0 z-0 h-full w-full bg-radial-[at_45%_68%] from-[#2CD5FF]/35 via-[#2C30FF]/2 mask-[linear-gradient(to_bottom,transparent,black_100%)] blur-[50px]" />

            <div className="relative z-10 flex flex-col items-center justify-center text-center space-y-6 max-w-4xl mx-auto">
              <HeaderBadge
                icon={<Terminal className="size-4" />}
                text="Now Available"
              />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-balance">
                Video generation,{' '}
                <Highlight>from your terminal</Highlight>
              </h1>
              <p className="text-secondary-foreground/70 text-center text-balance text-lg max-w-2xl mx-auto">
                Footage CLI brings the full power of the studio to your command line. An AI creative director that plans, generates, and manages your videos.
              </p>

              <div className="pt-2 w-full max-w-lg">
                <InstallSnippet />
                <p className="text-xs text-muted-foreground mt-3">
                  Then run <span className="font-mono text-foreground/80">footage login</span> to connect your account
                </p>
              </div>
            </div>
          </section>

          {/* ── Terminal demo ────────────────────────────────────── */}
          <section className="grid grid-cols-1 md:grid-cols-6 divide-y md:divide-y-0 md:divide-x divide-border">
            <div className="col-span-2 p-6 md:p-12 flex flex-col justify-center">
              <div className="md:sticky md:top-20 md:self-start space-y-4">
                <h2 className="text-2xl md:text-3xl font-medium tracking-tighter text-balance">
                  Your studio,{' '}
                  <Highlight>scriptable</Highlight>
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Log in, scan your local media, and tell the AI what you want to make. It handles the rest — picking models, writing prompts, and organizing clips into your project.
                </p>
              </div>
            </div>
            <div className="col-span-4 p-6 md:p-12">
              <TerminalDemo />
            </div>
          </section>

          {/* ── Commands ─────────────────────────────────────────── */}
          <section className="p-6 md:p-12 lg:p-16">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-2xl md:text-3xl font-medium tracking-tighter">
                One tool, every workflow
              </h2>
              <p className="text-muted-foreground text-sm max-w-lg mx-auto">
                Simple, composable commands that do exactly what you'd expect. Chain them together to build any video pipeline.
              </p>
              <CommandList />
            </div>
          </section>

          {/* ── Features grid ────────────────────────────────────── */}
          <section className="grid grid-cols-1 md:grid-cols-2">
            {features.map((f, i) => (
              <FeatureCard key={f.title} {...f} index={i} />
            ))}
          </section>

          {/* ── CTA / Install ─────────────────────────────────────── */}
          <section className="relative flex flex-col items-center justify-center px-4 py-20 md:py-32 overflow-hidden">
            <div className="absolute inset-0 z-0 h-full w-full bg-radial-[at_45%_85%] from-[#2CD5FF]/40 via-[#2C30FF]/4 mask-[linear-gradient(to_bottom,transparent,black_100%)]" />
            <div className="absolute inset-0 z-0 h-full w-full bg-radial-[at_45%_68%] from-[#2CD5FF]/68 via-[#2C30FF]/3 mask-[linear-gradient(to_bottom,transparent,black_100%)] blur-[50px]" />

            <div className="relative z-10 flex flex-col items-center justify-center text-center space-y-6 max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-balance">
                Get started in seconds
              </h2>
              <div className="font-mono text-sm text-muted-foreground space-y-1.5 text-left">
                <p><span className="text-sky-400">$</span> curl -fsSL {SITE_HOST}/install.sh | sh</p>
                <p><span className="text-sky-400">$</span> footage login</p>
                <p><span className="text-sky-400">$</span> footage "make me a product demo video"</p>
              </div>
              <InstallSnippet className="pt-2" />
              <p className="text-xs text-muted-foreground/60 pt-2">
                macOS and Linux. Uses your existing {SITE_HOST} account and credits.
              </p>
            </div>
          </section>

          <HomepageFooter />
        </main>
      </div>
    </div>
  )
}
